import { FC, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import API from 'core/Api';
import { setAuthTokensFromResponse } from 'utils/auth';
import setServerErrors from 'utils/helpers/formErrors';
import { createErrorFromResponse } from 'core/Error';
import { usePermissionsContext } from 'store/permissions';
import { useTranslation } from 'react-i18next';

interface IProps {
  goBack: () => void;
  redirectState?: {
    from?: {
      pathname: string;
    };
    email?: string;
  };
}

interface IInputs {
  code: string;
}

const AuthCodeForm: FC<IProps> = ({ goBack, redirectState }: IProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const methods = useForm<IInputs>();
  const { setPermissions } = usePermissionsContext();
  const codeRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    codeRef?.current?.focus();
  }, []);
  const onSubmit = (data: IInputs) => {
    return API.post('auth/2fa-login', data)
      .then(async res => {
        // Set tokens
        await setAuthTokensFromResponse(res);

        const permissions = await API.get('permissions');

        setPermissions(permissions.data);

        /**
         * Redirect user back to the same page where he was operating
         * or trying to load the page, when he got logged out
         * because of expired access and refresh tokens.
         * Exception here makes the /logout route itself
         * because of the change password functionality:
         * - once the user changes his password, his tokens
         * are no longer valid and he gets logged out but
         * `/auth/logout` request fails. So in this case if
         * the user has been operating on logout screen last
         * do not redirect to logout after login.
         */
        if (redirectState?.from!.pathname) return history.push(redirectState.from.pathname);

        return history.push('/home');
      })
      .catch(err => setServerErrors(createErrorFromResponse(err).errors, methods.setError));
  };
  return (
    <div>
      <Form
        methods={methods}
        onSubmit={onSubmit}
        btnLabel={t('auth.login.login')}
        hideCancelBtn
      >
        <FormGroup
          controlId="formGroupCode"
          label={t('auth.login.code')}
          type="tel"
          placeholder="******"
          name="code"
          setRef={ref => {
            codeRef.current = ref;
          }}
          autoComplete="off"
          isRequired
          isVerificationCode
          data-testid="login-code-input"
        />
      </Form>
      <div className="text-center mt-3">
        <Link
          to="#"
          onClick={goBack}
        >
          {t('common.back')}
        </Link>
      </div>
    </div>
  );
};

export default AuthCodeForm;
